import {
  columnIndex,
  numberValue,
  stringValue,
} from "./excelValueUtils";

export interface WorksheetExpectedContentCell {
  address: string;
  kind: "formula" | "value" | "blank";
  expected: string | number | boolean | null;
}

export interface WorksheetContentReadbackTarget {
  sheetName: string;
  address: string;
  rowCount: number;
  columnCount: number;
  cells: WorksheetExpectedContentCell[];
}

export interface WorksheetContentReadbackEvaluation {
  complete: boolean;
  reason?: string;
  missingCells: WorksheetExpectedContentCell[];
}

interface ParsedA1Range {
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
}

/**
 * Verifies structured worksheet content without coercing typed static values.
 *
 * Numbers, booleans, and strings must retain their runtime type. Formula and
 * blank expectations use their dedicated comparison rules.
 */
export function evaluateWorksheetContentReadback(
  target: WorksheetContentReadbackTarget,
  parsedArgs: unknown,
  parsedOutput: unknown,
): WorksheetContentReadbackEvaluation {
  const args = recordValue(parsedArgs);
  const output = recordValue(parsedOutput);
  const qualifiedTarget = `${target.sheetName}!${target.address}`;
  if (output.truncated === true) {
    return {
      complete: false,
      reason: `${qualifiedTarget} read-back was truncated`,
      missingCells: target.cells,
    };
  }
  const outputAddress = stringValue(output.address);
  const outputRange = outputAddress ? parseA1Range(outputAddress) : undefined;
  const targetRange = parseA1Range(target.address);
  if (!outputRange || !targetRange) {
    return {
      complete: false,
      reason:
        `${qualifiedTarget} was not returned at full shape` +
        (outputAddress ? ` (host returned ${outputAddress})` : ""),
      missingCells: target.cells,
    };
  }
  let shapeReason: string | undefined;
  if (!rangeContains(outputRange, targetRange)) {
    shapeReason =
      `${qualifiedTarget} was not returned at full shape` +
      (outputAddress ? ` (host returned ${outputAddress})` : "");
  }
  const rowCount = numberValue(output.rowCount);
  const columnCount = numberValue(output.columnCount);
  if (
    rowCount === undefined ||
    columnCount === undefined ||
    rowCount < target.rowCount ||
    columnCount < target.columnCount
  ) {
    shapeReason =
      `${qualifiedTarget} expected ${target.rowCount}×${target.columnCount}, ` +
      `but read-back reported ${rowCount ?? 0}×${columnCount ?? 0}`;
  }

  const requiresFormulas = target.cells.some(
    (cell) => cell.kind === "formula" || cell.kind === "blank",
  );
  if (
    requiresFormulas &&
    (args.format !== "json" ||
      args.includeFormulas !== true ||
      !Array.isArray(output.formulas))
  ) {
    return {
      complete: false,
      reason:
        `${qualifiedTarget} contains formulas but was not read as JSON with includeFormulas:true`,
      missingCells: target.cells.filter(
        (cell) => cell.kind === "formula" || cell.kind === "blank",
      ),
    };
  }
  if (!Array.isArray(output.values)) {
    return {
      complete: false,
      reason: `${qualifiedTarget} did not return a structured values matrix`,
      missingCells: target.cells,
    };
  }
  if (!hasDenseWorksheetContentReadbackEvidence(target, output)) {
    return {
      complete: false,
      reason:
        `${qualifiedTarget} did not return dense value/formula matrices for every requested coordinate`,
      missingCells: target.cells,
    };
  }

  const values = output.values as unknown[][];
  const formulas = Array.isArray(output.formulas)
    ? (output.formulas as unknown[][])
    : [];
  const missingCells = target.cells.filter((cell) => {
    const position = parseA1Cell(cell.address);
    if (!position) return true;
    const row = position.row - outputRange.startRow;
    const column = position.column - outputRange.startColumn;
    if (row < 0 || column < 0) return true;
    if (cell.kind === "formula") {
      return !equivalentFormula(formulas[row]?.[column], cell.expected);
    }
    if (cell.kind === "blank") {
      return (
        !isBlankReadbackValue(values[row]?.[column]) ||
        !isBlankReadbackValue(formulas[row]?.[column])
      );
    }
    return !equivalentStaticValue(values[row]?.[column], cell.expected);
  });
  if (missingCells.length > 0) {
    return {
      complete: false,
      reason:
        `${qualifiedTarget} is missing or mismatched at ` +
        `${missingCells
          .slice(0, 8)
          .map((cell) => cell.address)
          .join(", ")}` +
        (missingCells.length > 8
          ? ` and ${missingCells.length - 8} more`
          : ""),
      missingCells,
    };
  }
  if (shapeReason) {
    return {
      complete: false,
      reason: shapeReason,
      missingCells: [],
    };
  }
  return { complete: true, missingCells: [] };
}

export function hasDenseWorksheetContentReadbackEvidence(
  target: WorksheetContentReadbackTarget,
  output: Record<string, unknown>,
): boolean {
  if (
    output.evidenceSource === "csv_fallback" &&
    target.cells.some(
      (cell) =>
        typeof cell.expected === "number" ||
        typeof cell.expected === "boolean",
    )
  ) {
    return false;
  }
  const outputAddress = stringValue(output.address);
  const outputRange = outputAddress ? parseA1Range(outputAddress) : undefined;
  const targetRange = parseA1Range(target.address);
  if (
    !outputRange ||
    !targetRange ||
    !rangeContains(outputRange, targetRange) ||
    output.truncated === true
  ) {
    return false;
  }
  const rowCount = numberValue(output.rowCount);
  const columnCount = numberValue(output.columnCount);
  if (
    rowCount === undefined ||
    columnCount === undefined ||
    rowCount < target.rowCount ||
    columnCount < target.columnCount
  ) {
    return false;
  }
  const startRow = targetRange.startRow - outputRange.startRow;
  const startColumn = targetRange.startColumn - outputRange.startColumn;
  const coversRectangle = (matrix: unknown): boolean => {
    if (!Array.isArray(matrix)) return false;
    for (let rowOffset = 0; rowOffset < target.rowCount; rowOffset += 1) {
      const row = matrix[startRow + rowOffset];
      if (!Array.isArray(row)) return false;
      for (
        let columnOffset = 0;
        columnOffset < target.columnCount;
        columnOffset += 1
      ) {
        if (!(startColumn + columnOffset in row)) return false;
      }
    }
    return true;
  };
  if (!coversRectangle(output.values)) return false;
  const requiresFormulas = target.cells.some(
    (cell) => cell.kind === "formula" || cell.kind === "blank",
  );
  return !requiresFormulas || coversRectangle(output.formulas);
}

function recordValue(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function equivalentStaticValue(actual: unknown, expected: unknown): boolean {
  if (typeof expected === "number") {
    return typeof actual === "number" && Object.is(actual, expected);
  }
  if (typeof expected === "boolean") return actual === expected;
  return typeof actual === "string" && actual === expected;
}

function equivalentFormula(actual: unknown, expected: unknown): boolean {
  if (typeof actual !== "string") return false;
  const normalizedActual = normalizeFormulaForComparison(actual);
  if (normalizedActual === "") return false;
  if (typeof expected !== "string") return true;
  const normalizedExpected = normalizeFormulaForComparison(expected);
  if (normalizedExpected === "") return true;
  return normalizedActual === normalizedExpected;
}

function normalizeFormulaForComparison(formula: string): string {
  return formula
    .trim()
    .replace(/^=/, "")
    .replace(/\s+/g, " ")
    .replace(/\s*([(),;])\s*/g, "$1")
    .trim()
    .toUpperCase();
}

function isBlankReadbackValue(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

function parseA1Range(address: string): ParsedA1Range | undefined {
  const localAddress = address.includes("!")
    ? address.slice(address.lastIndexOf("!") + 1)
    : address;
  const [startRaw, endRaw = startRaw] = localAddress.split(":");
  const start = parseA1Cell(startRaw);
  const end = parseA1Cell(endRaw);
  if (!start || !end) return undefined;
  return {
    startRow: Math.min(start.row, end.row),
    endRow: Math.max(start.row, end.row),
    startColumn: Math.min(start.column, end.column),
    endColumn: Math.max(start.column, end.column),
  };
}

function parseA1Cell(
  address: string | undefined,
): { row: number; column: number } | undefined {
  const match = /^([A-Z]+)(\d+)$/i.exec(
    address?.replace(/\$/g, "").trim() ?? "",
  );
  if (!match) return undefined;
  return {
    row: Number(match[2]) - 1,
    column: columnIndex(match[1]),
  };
}

function rangeContains(
  container: ParsedA1Range,
  target: ParsedA1Range,
): boolean {
  return (
    container.startRow <= target.startRow &&
    container.endRow >= target.endRow &&
    container.startColumn <= target.startColumn &&
    container.endColumn >= target.endColumn
  );
}
